/*
 * Copyright (c) Nexign, JSC, 2019-2020.
 */
/**
 * Select component for CreateInquiryForm
 *
 * @author Evgenij Tyan
 * @author Yakov Gushchin
 * @author Anton Panasenko
 * @version 1.21.0
 * @since 1.0.0
 */
import { Fragment } from 'react';

import { FieldProps } from 'formik';
import invoke from 'lodash/invoke';

import { Select } from '@components/common';
import { DropDownFilterButtonSize } from '@components/common/DropDownFilter/DropDownFilter';
import { LabelStyled } from '@components/common/Input/styled/InputStyled';
import { IOptionItemProps } from '@components/common/Select/Select';

import { ETopicAttrVisibility } from '../../types/createInquiryTypes';
import { ErrorMessage, FieldRequiredStyled, FormItemContainerStyled } from './styled/CreateInquiryFormStyled';

export interface ICreateInquiryFormSelect {
    label: string;
    onSelect?: (item: IOptionItemProps) => void;
    options: IOptionItemProps[];
    placeholder?: string;
    required?: boolean;
    visibility: ETopicAttrVisibility;
}

export const CreateInquiryFormSelect = ({
    field,
    form: { touched, errors, setFieldValue },
    ...props
}: FieldProps & ICreateInquiryFormSelect) => {
    const { options } = props;
    const selectedOption = options ? options
        .find(item => item.value === field.value)
        : undefined;

    const onSelectHandler = (item: IOptionItemProps) => {
        setFieldValue(field.name, item.value);
        invoke(props, 'onSelect', item);
    };

    return (
        <FormItemContainerStyled>
            <LabelStyled htmlFor={field.name} kind="normal">
                {props.label}
                {
                    props.required &&
                    <FieldRequiredStyled>*</FieldRequiredStyled>
                }
            </LabelStyled>
            {
                props.visibility !== ETopicAttrVisibility.READ_ONLY &&
                <Fragment>
                    <Select
                        {...field}
                        {...props}
                        filterButtonSize={DropDownFilterButtonSize.medium}
                        options={options}
                        placeholder={props.placeholder}
                        selectedOption={selectedOption}
                        virtualListProps={{
                            isLoading: false,
                            hasMore: false,
                            rowHeight: 32,
                            onScrollEvent: () => {},
                        }}
                        onSelect={onSelectHandler}
                    />
                    {
                        touched[field.name] &&
                        errors[field.name] &&
                        <ErrorMessage>
                            {
                                errors[field.name]
                            }
                        </ErrorMessage>
                    }
                </Fragment>
            }
        </FormItemContainerStyled>
    );
};
